"use client"

import { usePathname } from "next/navigation"
import { CTA } from "@/components/sections/CTA"
import { type Locale } from "@/lib/i18n"

export default function KVKKPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  
  return (
    <>
      <section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
              KVKK Aydınlatma Metni
            </h1>
            <p className="text-lg text-white/90 sm:text-xl mb-2">
              6698 sayılı Kişisel Verilerin Korunması Kanunu uyarınca hazırlanmıştır.
            </p>
            <p className="text-base text-white/80">
              Son güncelleme: {new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </section>

      <section className="pt-12 pb-12 lg:pb-16 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Content */}
            <div className="prose prose-slate max-w-none space-y-6 sm:space-y-8 text-sm sm:text-base">
              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">1. Veri Sorumlusu</h2>
                <p>
                  6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") uyarınca, kişisel verileriniz aşağıda belirtilen 
                  çerçevede işlenecektir.
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm sm:text-base">
                  <p className="font-semibold mb-2">LAK VERSE</p>
                  <p className="text-sm text-slate-600">
                    E-posta: info@lakverse.com<br />
                    Web: www.lakverse.com
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">2. İşlenen Kişisel Veriler</h2>
                <p>
                  Hizmetlerimiz kapsamında, aşağıdaki kategorilerdeki kişisel verileriniz işlenmektedir:
                </p>

                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mt-4 sm:mt-6 mb-2 sm:mb-3">2.1. Kimlik Verileri</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Ad, soyad</li>
                  <li>TC Kimlik numarası (yasal zorunluluklar gereği)</li>
                  <li>Doğum tarihi</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">2.2. İletişim Verileri</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>E-posta adresi</li>
                  <li>Telefon numarası</li>
                  <li>Adres bilgileri</li>
                  <li>Faks numarası</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">2.3. Müşteri İşlem Verileri</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Proje talepleri</li>
                  <li>Teklif bilgileri</li>
                  <li>Sözleşme bilgileri</li>
                  <li>Fatura bilgileri</li>
                  <li>Ödeme bilgileri</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">2.4. Pazarlama Verileri</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Pazarlama ve iletişim tercihleri</li>
                  <li>Kampanya bilgileri</li>
                  <li>Abonelik bilgileri</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">2.5. İşlem Güvenliği Verileri</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>IP adresi</li>
                  <li>Çerez (cookie) bilgileri</li>
                  <li>Log kayıtları</li>
                  <li>Tarayıcı bilgileri</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">2.6. Görsel ve İşitsel Kayıtlar</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Fotoğraf (proje görselleri, etkinlik fotoğrafları)</li>
                  <li>Video kayıtları (proje sunumları)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">3. Kişisel Verilerin İşlenme Amaçları</h2>
                <p>Kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:</p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Hizmet taleplerinizin değerlendirilmesi ve karşılanması</li>
                  <li>Teklif hazırlama, sunma ve sözleşme süreçlerinin yürütülmesi</li>
                  <li>Proje yönetimi, takibi ve teslimi</li>
                  <li>Müşteri ilişkileri yönetimi ve müşteri memnuniyetinin artırılması</li>
                  <li>İletişim faaliyetlerinin yürütülmesi</li>
                  <li>Fatura, fatura düzenleme ve muhasebe işlemlerinin yürütülmesi</li>
                  <li>Yasal yükümlülüklerin yerine getirilmesi</li>
                  <li>Hukuki uyuşmazlıkların çözümü</li>
                  <li>Web sitesi analizi ve kullanıcı deneyiminin iyileştirilmesi</li>
                  <li>Güvenlik ve dolandırıcılık önleme faaliyetleri</li>
                  <li>Pazarlama ve tanıtım faaliyetleri (açık rıza ile)</li>
                  <li>E-bülten ve kampanya gönderimi (açık rıza ile)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">4. Kişisel Verilerin İşlenmesinin Hukuki Sebepleri</h2>
                <p>Kişisel verileriniz aşağıdaki hukuki sebeplerle işlenmektedir:</p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>
                    <strong>KVKK md. 5/2-a:</strong> Açık rızanızın bulunması
                  </li>
                  <li>
                    <strong>KVKK md. 5/2-c:</strong> Sözleşmenin kurulması veya ifası ile doğrudan ilgili olması
                  </li>
                  <li>
                    <strong>KVKK md. 5/2-e:</strong> Veri sorumlusunun hukuki yükümlülüğünü yerine getirebilmesi için zorunlu olması
                  </li>
                  <li>
                    <strong>KVKK md. 5/2-f:</strong> İşlenen verilerin ilgili kişinin kendisi tarafından alenileştirilmiş olması
                  </li>
                  <li>
                    <strong>KVKK md. 5/2-ç:</strong> Bir hakkın tesisi, kullanılması veya korunması için veri işlemenin zorunlu olması
                  </li>
                  <li>
                    <strong>KVKK md. 5/2-f:</strong> Meşru menfaatlerimiz için veri işlemenin zorunlu olması
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">5. Kişisel Verilerin Aktarılması</h2>
                <p>
                  Kişisel verileriniz, yukarıda belirtilen amaçların gerçekleştirilmesi için aşağıdaki üçüncü kişilere 
                  aktarılabilir:
                </p>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">5.1. Yurt İçi Aktarımlar</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Yasal yükümlülüklerimiz gereği kamu kurum ve kuruluşları</li>
                  <li>Hizmet sağlayıcılarımız (hosting, bulut depolama, e-posta servisleri)</li>
                  <li>Analitik ve pazarlama araçları sağlayıcıları</li>
                  <li>Ödeme işlem şirketleri</li>
                  <li>Yasal danışmanlarımız ve muhasebe firmaları</li>
                  <li>İş ortaklarımız ve tedarikçilerimiz</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">5.2. Yurt Dışı Aktarımlar</h3>
                <p>
                  Kişisel verileriniz, hizmet sağlayıcılarımızın bulunduğu yurt dışı ülkelere aktarılabilir. Bu aktarımlar, 
                  KVKK md. 9 hükümleri ve Kişisel Verileri Koruma Kurulu kararları çerçevesinde, yeterli korumanın bulunduğu 
                  ülkelere veya yeterli korumanın bulunmadığı ülkelerde yazılı taahhütname ile yapılmaktadır.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">6. İlgili Kişinin Hakları (KVKK md. 11)</h2>
                <p>KVKK md. 11 uyarınca, aşağıdaki haklara sahipsiniz:</p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>
                    <strong>Bilgi talep etme:</strong> Kişisel verilerinizin işlenip işlenmediğini öğrenme, işlenmişse 
                    buna ilişkin bilgi talep etme
                  </li>
                  <li>
                    <strong>İşleme amacını öğrenme:</strong> İşlenme amacını ve bunların amacına uygun kullanılıp 
                    kullanılmadığını öğrenme
                  </li>
                  <li>
                    <strong>Yurt içi/yurt dışı aktarım bilgisi:</strong> Yurt içinde veya yurt dışında aktarıldığı 
                    üçüncü kişileri bilme
                  </li>
                  <li>
                    <strong>Düzeltme talep etme:</strong> Eksik veya yanlış işlenmişse düzeltilmesini isteme
                  </li>
                  <li>
                    <strong>Silme/yok etme talep etme:</strong> KVKK md. 7'de öngörülen şartlar çerçevesinde silinmesini 
                    veya yok edilmesini isteme
                  </li>
                  <li>
                    <strong>Üçüncü kişilere bildirim:</strong> Düzeltme, silme, yok edilme işlemlerinin, kişisel verilerin 
                    aktarıldığı üçüncü kişilere bildirilmesini isteme
                  </li>
                  <li>
                    <strong>İtiraz etme:</strong> İşlenen verilerin münhasıran otomatik sistemler ile analiz edilmesi 
                    suretiyle kişinin kendisi aleyhine bir sonucun ortaya çıkmasına itiraz etme
                  </li>
                  <li>
                    <strong>Zararın giderilmesini talep etme:</strong> Kişisel verilerin kanuna aykırı olarak işlenmesi 
                    sebebiyle zarara uğraması halinde zararın giderilmesini talep etme
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">7. Başvuru Yöntemi</h2>
                <p>
                  Yukarıda belirtilen haklarınızı kullanmak için, kimliğinizi tespit edici belgelerle birlikte aşağıdaki 
                  yöntemlerle başvurabilirsiniz:
                </p>
                
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm sm:text-base">
                  <p className="font-semibold mb-2">E-posta ile Başvuru:</p>
                  <p className="text-sm text-slate-600 mb-4">
                    info@lakverse.com
                  </p>
                  
                  <p className="font-semibold mb-2">Yazılı Başvuru:</p>
                  <p className="text-sm text-slate-600">
                    LAKVERSE<br />
                    Workinton Carousel, Fişekhane Cad. No: 5, Carousel AVM 2B Katı, 34710 Bakırköy / İstanbul
                  </p>
                </div>

                <p className="mt-4">
                  Başvurunuzda, talep ettiğiniz hak ve konuya ilişkin açıklamalarınızı belirtmeniz gerekmektedir. Başvurunuz, 
                  talebinizin niteliğine göre en geç <strong>30 (otuz) gün</strong> içinde sonuçlandırılacaktır.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">8. İletişim</h2>
                <p>
                  KVKK kapsamındaki haklarınız ve kişisel verilerinizin işlenmesi hakkında sorularınız için bizimle 
                  iletişime geçebilirsiniz:
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm sm:text-base">
                  <p className="font-semibold mb-2">LAK VERSE</p>
                  <p className="text-sm text-slate-600">
                    E-posta: info@lakverse.com<br />
                    Web: www.lakverse.com
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
      <CTA locale={currentLocale} />
    </>
  )
}
